// Declared once in `shared/organizations/organization.ts`, alongside the `*Row` variants the service
// functions in `server/services/organizations` are annotated with. These were local interfaces until
// 2026-08-26, maintained by hand beside SQL projections nothing checked them against.
import type {
  OrganizationInfo,
  OrganizationInvite,
  OrganizationInviteCreated,
  OrganizationInviteResent,
  OrganizationMember,
  OrganizationRolesUpdated,
  OrganizationSettingsUpdated,
  StorageBackend,
} from '#shared/organizations/organization'
import type {
  CustomRoleDefinition,
  PermissionCapability,
  ToggleableCapability,
} from '#shared/organizations/permissions'

export function useOrgManagement() {
  const { apiFetch } = useApi()
  const org = ref<OrganizationInfo | null>(null)
  const members = ref<OrganizationMember[]>([])
  const invites = ref<OrganizationInvite[]>([])
  const loading = ref(false)

  const isAdmin = computed(() => org.value?.role === 'admin')

  function hasCapability(capability: PermissionCapability): boolean {
    if (!org.value) return false
    if (org.value.role === 'admin') return true
    return Array.isArray(org.value.capabilities) && org.value.capabilities.includes(capability)
  }

  async function fetchOrg() {
    loading.value = true
    try {
      org.value = await apiFetch<OrganizationInfo>('/api/orgs/info')
    } finally {
      loading.value = false
    }
  }

  async function updateOrg(name: string) {
    const result = await apiFetch<OrganizationSettingsUpdated>('/api/orgs/info', {
      method: 'PUT',
      body: { name },
    })
    if (org.value) {
      org.value.name = result.name
    }
  }

  async function updateStorageBackend(backend: StorageBackend) {
    const result = await apiFetch<OrganizationSettingsUpdated>('/api/orgs/info', {
      method: 'PUT',
      body: { storage_backend: backend },
    })
    if (org.value) {
      org.value.storage_backend = result.storage_backend
    }
  }

  async function updateMainJurisdiction(jurisdiction: string) {
    const result = await apiFetch<OrganizationSettingsUpdated>('/api/orgs/info', {
      method: 'PUT',
      body: { main_jurisdiction: jurisdiction },
    })
    if (org.value) {
      org.value.main_jurisdiction = result.main_jurisdiction
    }
  }

  /** MAR-1418: the jurisdictions the org may also use in chat. The server drops main and checks the corpus. */
  async function updateAdditionalJurisdictions(jurisdictions: string[]) {
    const result = await apiFetch<OrganizationSettingsUpdated>('/api/orgs/info', {
      method: 'PUT',
      body: { additional_jurisdictions: jurisdictions },
    })
    if (org.value) {
      org.value.additional_jurisdictions = result.additional_jurisdictions
    }
  }

  async function updateRequireMfa(requireMfa: boolean) {
    const result = await apiFetch<OrganizationSettingsUpdated>('/api/orgs/info', {
      method: 'PUT',
      body: { require_mfa: requireMfa },
    })
    if (org.value) {
      org.value.require_mfa = result.require_mfa
    }
    return result
  }

  async function updateRoles(payload: {
    member_capabilities?: ToggleableCapability[]
    custom_roles?: Array<Pick<CustomRoleDefinition, 'name' | 'capabilities'> & { slug?: string; description?: string }>
  }) {
    const result = await apiFetch<OrganizationRolesUpdated>('/api/orgs/roles', {
      method: 'PUT',
      body: payload,
    })
    if (org.value) {
      org.value.role_definitions = result.role_definitions
      org.value.roles = result.roles
    }
    return result
  }

  async function fetchMembers() {
    members.value = await apiFetch<OrganizationMember[]>('/api/orgs/members')
  }

  async function updateMemberRole(memberId: string, role: string) {
    await apiFetch(`/api/orgs/members/${memberId}`, {
      method: 'PUT',
      body: { role },
    })
    const member = members.value.find(m => m.id === memberId)
    if (member) member.role = role
  }

  async function removeMember(memberId: string) {
    await apiFetch(`/api/orgs/members/${memberId}`, { method: 'DELETE' })
    members.value = members.value.filter(m => m.id !== memberId)
    if (org.value) org.value.member_count = members.value.length
  }

  async function fetchInvites() {
    invites.value = await apiFetch<OrganizationInvite[]>('/api/orgs/invites')
  }

  async function createInvite(opts: { email?: string | null; role?: string; plan_id?: string | null; max_uses?: number | null; expires_in_hours?: number; quote_id?: string }) {
    const invite = await apiFetch<OrganizationInviteCreated>('/api/orgs/invites', {
      method: 'POST',
      body: opts,
    })
    // `POST /api/orgs/invites` returns the INSERT's `RETURNING` list, which never joins `users` — so
    // it carries no `created_by_name` and the list row is completed here rather than by widening the
    // create query. The name appears on the next `fetchInvites()`; the column rendered blank for the
    // freshly-created row before this too, when the field was simply absent.
    invites.value.unshift({ ...invite, created_by_name: null })
    return invite
  }

  async function resendInvite(inviteId: string) {
    return await apiFetch<OrganizationInviteResent>(`/api/orgs/invites/${inviteId}/resend`, {
      method: 'POST',
    })
  }

  async function revokeInvite(inviteId: string) {
    await apiFetch(`/api/orgs/invites/${inviteId}`, { method: 'DELETE' })
    invites.value = invites.value.filter(i => i.id !== inviteId)
  }

  return {
    org, members, invites, loading, isAdmin, hasCapability,
    fetchOrg, updateOrg, updateStorageBackend, updateMainJurisdiction, updateAdditionalJurisdictions, updateRequireMfa, updateRoles,
    fetchMembers, updateMemberRole, removeMember,
    fetchInvites, createInvite, resendInvite, revokeInvite,
  }
}
